/**
 * 仕入の一覧 /purchases（#233 3a、docs/93 §15.4・§15.10 ①-c）と {project}のタブ「仕入」（GroupPurchases）。左のメニュー「仕入」（{order}の下・両製品・門 sales）。
 *   タブ：未払い（残のある仕入＝一部支払も含む）／一部支払／支払済／下書き／すべて（件数つき。#233 3c）。探す（{vendor}・請求書番号・{project}）。
 *   列：請求日・{vendor}・請求書番号・{project}・金額（税込）・支払期日（期限超過は朱・60日超）・残・状態
 *   「仕入登録」は PC だけ（/purchases/new。スマホは閲覧のみ）
 */
import { PURCHASE_MSG, PURCHASE_TAB_LABEL, type PurchaseList, type PurchaseSummary, type PurchaseTab } from "@genbaryoku/contract";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { api, messageOf } from "../../api.js";
import { usePageName } from "../../components/BackButton.jsx";
import { FilterInput, ListHead } from "../../components/ListHead.jsx";
import { ListTabs, listTabPanel } from "../../components/ListTabs.jsx";
import { Icon } from "../../components/Shell.jsx";
import { ErrorText } from "../../components/ui.jsx";
import { t, useLabels } from "../../labels.js";
import { mdSlash, yen } from "../../lib/num.js";
import { usePcOnly } from "../../lib/pcOnly.js";
import { DueCell, PurchaseStateChip } from "./parts.jsx";
import { PurchaseKindSwitch } from "../payment-notices/parts.jsx";

const TABS: PurchaseTab[] = ["unpaid", "partial", "paid", "draft", "all"];

export function PurchaseListPage() {
  const [sp, setSp] = useSearchParams();
  const tab = (TABS.find((k) => k === sp.get("tab")) ?? "all") as PurchaseTab;
  usePageName(PURCHASE_MSG.list);
  return (
    <ListHead title={PURCHASE_MSG.list} extra={<PurchaseKindSwitch value="purchases" />}>
      <PurchaseTable
        create={<CreateLink />}
        tab={tab}
        onTab={(k) =>
          setSp((p) => {
            const n = new URLSearchParams(p);
            if (k === "all") n.delete("tab");
            else n.set("tab", k);
            return n;
          })
        }
      />
    </ListHead>
  );
}

/** {project}の画面のタブ「仕入」（件数・合計・残と「仕入登録」） */
export function GroupPurchases({ groupId }: { groupId: string }) {
  const [tab, setTab] = useState<PurchaseTab>("all");
  return (
    <div data-testid="group-purchases">
      <PurchaseTable tab={tab} onTab={setTab} group={groupId} side={<CreateLink group={groupId} />} />
    </div>
  );
}

function CreateLink({ group }: { group?: string }) {
  const pcOnly = usePcOnly();
  if (pcOnly.hidden("/purchases/new")) return null;
  return (
    <Link className={group ? "btn" : "btn primary"} to={`/purchases/new${group ? `?group=${group}` : ""}`} data-testid="purchase-new">
      {group ? null : <Icon k="plus" />}
      {PURCHASE_MSG.create}
    </Link>
  );
}

/** 一覧（create を渡すと一覧の頭の形＝表のカード・#241／{project}の画面のタブ＝side を件数の行の右に） */
function PurchaseTable({ tab, onTab, group, side, create }: { tab: PurchaseTab; onTab: (k: PurchaseTab) => void; group?: string; side?: ReactNode; create?: ReactNode }) {
  const L = useLabels();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  useEffect(() => {
    const h = window.setTimeout(() => setQApplied(q.trim()), 300);
    return () => window.clearTimeout(h);
  }, [q]);
  const list = useQuery({
    queryKey: ["purchases", tab, qApplied, group ?? ""],
    queryFn: () => api<PurchaseList>("GET", `/purchases?tab=${tab}${qApplied ? `&q=${encodeURIComponent(qApplied)}` : ""}${group ? `&group=${group}` : ""}`),
    placeholderData: keepPreviousData,
  });
  const items: PurchaseSummary[] = list.data?.items ?? [];
  const open = (p: PurchaseSummary) => nav(`/purchases/${p.id}`);
  const tid = group ? "gpu" : "pul";
  const search = <FilterInput value={q} onChange={setQ} placeholder={t(PURCHASE_MSG.searchLabel)} testId="purchase-search" />;
  const card = create !== undefined;
  const sum = (
    <div className="pul-sum" data-testid="purchase-sum">
      <span>
        <b className="num">{list.data ? items.length : "…"}</b> 件
      </span>
      <span>
        合計 <b className="num">{list.data ? yen(list.data.total) : "…"}</b> 円
      </span>
      <span>
        {PURCHASE_MSG.colBalance} <b className="num">{list.data ? yen(list.data.balance) : "…"}</b> 円
      </span>
      <span className="sp" />
      {side}
    </div>
  );
  return (
    <>
      {list.isError ? <ErrorText>{messageOf(list.error)}</ErrorText> : null}
      <div className={card ? "lcard" : undefined}>
      <ListTabs
        id={tid}
        label={PURCHASE_MSG.tabsLabel}
        tabs={TABS.map((k) => ({ key: k, label: PURCHASE_TAB_LABEL[k], count: list.data?.counts[k] ?? "…" }))}
        value={tab}
        onChange={(k) => onTab(k as PurchaseTab)}
        filter={search}
        create={create}
      />
      <div className="tbl-wrap ltab-panel" {...listTabPanel(tid, tab)}>
        {/* 件数・合計・残（{project}のタブは右に「仕入登録」） */}
        {card ? null : sum}
        <table className="list" data-testid="purchase-list">
          <thead>
            <tr>
              <th>{PURCHASE_MSG.colBillDate}</th>
              <th>{L.vendor}</th>
              <th>{PURCHASE_MSG.colInvoiceNo}</th>
              {group ? null : <th>{L.project}</th>}
              <th className="amt">{PURCHASE_MSG.colAmount}</th>
              <th>{PURCHASE_MSG.colDue}</th>
              <th className="amt">{PURCHASE_MSG.colBalance}</th>
              <th>{PURCHASE_MSG.colState}</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="r" tabIndex={0} onClick={() => open(p)} onKeyDown={(e) => e.key === "Enter" && e.target === e.currentTarget && open(p)} data-testid="purchase-row">
                <td className="num">{mdSlash(p.purchasedOn)}</td>
                <td>
                  <b>{p.vendorName}</b>
                </td>
                <td className="num">{p.vendorInvoiceNo || <span className="muted">—</span>}</td>
                {group ? null : <td>{p.group?.name ?? <span className="muted">—</span>}</td>}
                <td className="num amt">{yen(p.total)}</td>
                <td>{p.status === "confirmed" ? <DueCell dueOn={p.paymentDueOn} overdueDays={p.overdueDays} over60={p.over60} /> : <span className="num muted">{p.paymentDueOn ? mdSlash(p.paymentDueOn) : "—"}</span>}</td>
                <td className="num amt">{p.status === "confirmed" ? yen(p.balance) : <span className="muted">—</span>}</td>
                <td className="statecell">
                  {/* 期限超過は支払期日の欄の朱の文字で出す（チップは 未払い／一部支払） */}
                  <PurchaseStateChip s={p.payState === "overdue" ? (p.paid > 0 ? "partial" : "unpaid") : p.payState} />
                </td>
              </tr>
            ))}
            {list.data && !items.length ? (
              <tr className="emptyrows">
                <td colSpan={group ? 7 : 8}>{PURCHASE_MSG.empty}</td>
              </tr>
            ) : null}
          </tbody>
        </table>
        {/* #241：一覧の頭（表のカード）のときは、タブの段と表の見出しを接するため件数と合計の行は表の下 */}
        {card ? sum : null}
      </div>
      </div>
    </>
  );
}
